/**
 * Approval Workflow Service
 *
 * API client for approval workflow endpoints
 */

import apiClient from './api';
import type { PostReleasePermissionsDto, ReleaseCapabilitiesDto, ReleaseDetailDto, ReviewDiffDto } from '@/types/release';
import { BOARD_SEARCH_MAX_LENGTH } from '@/types/releaseBoard';
import type {
  BoardFilters,
  BoardItem,
  ReleaseBoardCounts,
  BoardFacets,
  ReleaseBoardPage,
  ReleaseBoardPageParams,
} from '@/types/releaseBoard';
import {
  BulkApproveResult,
  BulkSendResult,
  BulkUndoItem,
  BulkUndoResult,
  MentionablePerson,
  ApprovalWorkflow,
  ConfigureWorkflowRequest,
  SubmitForApprovalRequest,
  ApprovalDecisionRequest,
  RequestChangesRequest,
  ReleaseResult,
  CommentVersionKey,
  CommentReaction,
  AddCommentRequest,
  ChatSticker,
  ApprovalWorkflowResponse,
  PostApprovalsResponse,
  ApprovalHistoryResponse,
  CommentsResponse,
  PostApproval,
  ApprovalComment,
  ApprovalUser,
  CommentAttachment,
  CommentAttachmentPresign,
  CommentAttachmentType,
  ScheduleFromApprovalRequest,
  ScheduleFromApprovalResult,
} from '@/types/approval';

/** The state an item is in after a release was undone (the server decides). */
export type UndoReleaseStatus = 'DRAFT' | 'IN_REVIEW';

/** Only non-default filters go on the wire (query-string names of the board endpoints). */
function buildBoardFilterParams(filters: Partial<BoardFilters>): Record<string, string> {
  const params: Record<string, string> = {};
  const q = filters.q?.trim().slice(0, BOARD_SEARCH_MAX_LENGTH);
  if (q) params.q = q;
  if (filters.creatorId != null) params.creatorId = String(filters.creatorId);
  if (filters.labels && filters.labels.length > 0) params.labels = filters.labels.join(',');
  if (filters.version && filters.version !== 'all') params.version = filters.version;
  if (filters.neededBy) params.neededBy = filters.neededBy;
  if (filters.mediaType) params.mediaType = filters.mediaType;
  return params;
}

/**
 * Backend Zod schemas expect positive integers for ids in request bodies,
 * while frontend types keep ids as strings. Convert at this boundary.
 */
const ATTACHMENT_TYPES: ReadonlyArray<CommentAttachmentType> = ['IMAGE', 'VIDEO', 'DOCUMENT'];

const nullableNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** Defensive mapping of an AttachmentDto; anything malformed is dropped. */
export const mapAttachment = (raw: unknown): CommentAttachment | null => {
  if (typeof raw !== 'object' || raw === null) return null;
  const a = raw as Record<string, unknown>;
  const type = a.type as CommentAttachmentType;
  if (typeof a.id !== 'number' || !ATTACHMENT_TYPES.includes(type) || typeof a.url !== 'string') {
    return null;
  }
  return {
    id: a.id,
    type,
    filename: typeof a.filename === 'string' ? a.filename : '',
    mimeType: typeof a.mimeType === 'string' ? a.mimeType : '',
    fileSize: nullableNumber(a.fileSize) ?? 0,
    width: nullableNumber(a.width),
    height: nullableNumber(a.height),
    duration: nullableNumber(a.duration),
    url: a.url,
    thumbnailUrl: typeof a.thumbnailUrl === 'string' ? a.thumbnailUrl : null,
  };
};

const mapAttachments = (raw: unknown): CommentAttachment[] =>
  Array.isArray(raw)
    ? raw.map(mapAttachment).filter((a): a is CommentAttachment => a !== null)
    : [];

/** Tolerant: a missing / malformed `reactions` (older payload) becomes []. */
const mapReactions = (raw: unknown): CommentReaction[] => {
  if (!Array.isArray(raw)) return [];
  const out: CommentReaction[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    if (typeof r.emoji !== 'string' || !r.emoji) continue;
    const users = Array.isArray(r.users)
      ? r.users
          .filter(
            (u): u is { id: number; name: string } =>
              !!u &&
              typeof u === 'object' &&
              typeof (u as { id?: unknown }).id === 'number' &&
              typeof (u as { name?: unknown }).name === 'string'
          )
          .map((u) => ({ id: u.id, name: u.name }))
      : [];
    out.push({
      emoji: r.emoji,
      count: typeof r.count === 'number' ? r.count : users.length,
      reactedByMe: r.reactedByMe === true,
      users,
    });
  }
  return out;
};

interface CommentReactionsResponse {
  success: boolean;
  data: { commentId: number; reactions: unknown };
}

const toPositiveInt = (value: string | number | null | undefined): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : undefined;
};

const toPositiveIntList = (values: Array<string | number> | undefined): number[] | undefined =>
  values?.map((v) => toPositiveInt(v)).filter((n): n is number => n !== undefined);

/**
 * Approval Service
 *
 * Provides methods for:
 * - Workflow configuration
 * - Submitting posts for approval
 * - Reviewing approvals (approve/reject/request changes)
 * - Managing comments
 */
export const approvalService = {
  // ===========================
  // Workflow Configuration
  // ===========================

  /**
   * Get approval workflow configuration for a team
   */
  getWorkflowConfig: async (teamId: string): Promise<ApprovalWorkflow> => {
    const response = await apiClient.get<ApprovalWorkflowResponse>(
      `/api/teams/${teamId}/approval-workflow`
    );
    return response.data.data;
  },

  /**
   * Configure approval workflow for a team
   */
  configureWorkflow: async (
    teamId: string,
    config: ConfigureWorkflowRequest
  ): Promise<ApprovalWorkflow> => {
    const response = await apiClient.put<ApprovalWorkflowResponse>(
      `/api/teams/${teamId}/approval-workflow`,
      {
        ...config,
        bypassUserIds: toPositiveIntList(config.bypassUserIds),
        steps: config.steps?.map((step) => ({
          ...step,
          approverUserIds: toPositiveIntList(step.approverUserIds) ?? [],
        })),
      }
    );
    return response.data.data;
  },

  // ===========================
  // Post Approval Actions
  // ===========================

  /**
   * Submit a post for approval
   */
  submitForApproval: async (
    postId: string,
    data?: SubmitForApprovalRequest
  ): Promise<PostApproval> => {
    const response = await apiClient.post<{ success: boolean; data: PostApproval }>(
      `/api/Post/${postId}/submit-for-approval`,
      data || {}
    );
    return response.data.data;
  },

  /**
   * One-step release by a person who is creator AND reviewer in the brand (no review record to wait for).
   * Errors carry `code` release.* (not_release_lead, already_in_review, already_released, frozen_committed,
   * workflow_inactive, empty_content).
   */
  releaseDirect: async (postId: number): Promise<ReleaseResult> => {
    const response = await apiClient.post<{ success: boolean; data: ReleaseResult }>(
      `/api/Post/${postId}/release-direct`,
      {}
    );
    return response.data.data;
  },

  /** Same as releaseDirect, for one variation. */
  releaseVariationDirect: async (postId: number, variationId: number): Promise<ReleaseResult> => {
    const response = await apiClient.post<{ success: boolean; data: ReleaseResult }>(
      `/api/Post/${postId}/variations/${variationId}/release-direct`,
      {}
    );
    return response.data.data;
  },

  /** What the viewer may do in a brand before any post exists (create mode). */
  getReleaseCapabilities: async (teamId: number): Promise<ReleaseCapabilitiesDto> => {
    const response = await apiClient.get<{ success: boolean; data: ReleaseCapabilitiesDto }>(
      `/api/teams/${teamId}/release-capabilities`
    );
    return response.data.data;
  },

  /**
   * Approve a post
   */
  approve: async (approvalId: string, data?: ApprovalDecisionRequest): Promise<ReleaseResult> => {
    const response = await apiClient.post<{ success: boolean; data: ReleaseResult }>(
      `/api/approvals/${approvalId}/approve`,
      data || {}
    );
    return response.data.data;
  },

  /**
   * Undo a release within its undo window (releaser only). Errors carry a
   * translated message (release.undo_expired / undo_not_allowed / undo_scheduled).
   */
  undoRelease: async (postId: number, variationId: number | null): Promise<UndoReleaseStatus> => {
    const response = await apiClient.post<{ success: boolean; data?: { status?: UndoReleaseStatus } }>(
      `/api/Post/${postId}/release/undo`,
      { variationId }
    );
    // DRAFT after a DIRECT release (it never was in review); anything else (or an older server): back in review
    return response.data?.data?.status === 'DRAFT' ? 'DRAFT' : 'IN_REVIEW';
  },

  /**
   * Take an item back out of review (sender / creator, only while it is still in review). Errors carry a
   * translated message (release.not_creator / withdraw_already_released / withdraw_changes_requested / withdraw_not_in_review).
   */
  withdraw: async (approvalId: number): Promise<{ approvalId: number; postId: number; variationId: number | null }> => {
    const response = await apiClient.post<{
      success: boolean;
      data: { approvalId: number; postId: number; variationId: number | null };
    }>(`/api/approvals/${approvalId}/withdraw`);
    return response.data.data;
  },

  /**
   * Request changes on a post
   */
  requestChanges: async (approvalId: string, data: RequestChangesRequest): Promise<PostApproval> => {
    const response = await apiClient.post<{ success: boolean; data: PostApproval }>(
      `/api/approvals/${approvalId}/request-changes`,
      { ...data, parentId: toPositiveInt(data.parentId) }
    );
    return response.data.data;
  },

  // ===========================
  // Query Methods
  // ===========================

  /**
   * One page of one Release Board column (cursor paged, filters applied server side)
   */
  getReleaseBoardPage: async (
    teamId: number,
    params: ReleaseBoardPageParams
  ): Promise<ReleaseBoardPage> => {
    const response = await apiClient.get<{ success: boolean; data: ReleaseBoardPage }>(
      `/api/teams/${teamId}/release-board`,
      {
        params: {
          ...buildBoardFilterParams(params),
          column: params.column,
          ...(params.cursor ? { cursor: params.cursor } : {}),
          limit: params.limit ?? 20,
        },
      }
    );
    return response.data.data;
  },

  /**
   * One board item (deep links). Rejects with a 404 when the item is not on the board.
   */
  getReleaseBoardItem: async (
    teamId: number,
    postId: number,
    variationId: number | null
  ): Promise<BoardItem> => {
    const response = await apiClient.get<{ success: boolean; data: BoardItem }>(
      `/api/teams/${teamId}/release-board/item`,
      { params: { postId, ...(variationId !== null ? { variationId } : {}) } }
    );
    return response.data.data;
  },

  /**
   * Filter facets of the board (which menus exist + their options)
   */
  getReleaseBoardFacets: async (teamId: number): Promise<BoardFacets> => {
    const response = await apiClient.get<{ success: boolean; data: BoardFacets }>(
      `/api/teams/${teamId}/release-board/facets`
    );
    return response.data.data;
  },

  /**
   * Real item counts of the four Release Board columns (same filters as the page endpoint)
   */
  getReleaseBoardCounts: async (
    teamId: number,
    filters: BoardFilters
  ): Promise<ReleaseBoardCounts> => {
    const response = await apiClient.get<{ success: boolean; data: ReleaseBoardCounts }>(
      `/api/teams/${teamId}/release-board/counts`,
      { params: buildBoardFilterParams(filters) }
    );
    return response.data.data;
  },

  /**
   * Get all approvals for a specific post
   */
  getPostApprovals: async (postId: string): Promise<PostApprovalsResponse['data']> => {
    const response = await apiClient.get<PostApprovalsResponse>(`/api/Post/${postId}/approvals`);
    return response.data.data;
  },

  /**
   * Get approval history for a post
   */
  getApprovalHistory: async (postId: string): Promise<ApprovalHistoryResponse['data']> => {
    const response = await apiClient.get<{
      success: boolean;
      data: Array<{
        id: number;
        status: string;
        requestedAt: string;
        reviewedAt: string | null;
        comment: string | null;
        autoApproved: boolean;
        /** Present once the backend exposes the variation a review belongs to (null = base post) */
        variationId?: number | null;
        step: { id: number; name: string } | null;
        requestedBy: { id: number; fullName: string; email: string } | null;
        reviewedBy: { id: number; fullName: string; email: string } | null;
        agencyUser: { id: number; fullName: string; email: string } | null;
      }>;
    }>(`/api/Post/${postId}/approval-history`);

    // Transform backend response to frontend format
    const history = response.data.data.flatMap((entry) => {
      const entries = [];

      // Add submission entry
      if (entry.requestedBy) {
        entries.push({
          id: `${entry.id}-submit`,
          action: 'SUBMITTED' as const,
          userId: String(entry.requestedBy.id),
          user: {
            id: String(entry.requestedBy.id),
            name: entry.requestedBy.fullName || entry.requestedBy.email,
            email: entry.requestedBy.email,
          },
          comment: undefined,
          variationId: entry.variationId,
          step: entry.step
            ? {
                ...entry.step,
                id: String(entry.step.id),
                workflowId: '',
                order: 0,
                approverRoles: [],
                approverUserIds: [],
                optional: false,
                createdAt: '',
                updatedAt: '',
              }
            : undefined,
          timestamp: entry.requestedAt,
        });
      }

      // Add review entry if reviewed
      if (entry.reviewedAt && entry.reviewedBy) {
        const actionMap: Record<string, 'APPROVED' | 'REJECTED' | 'CHANGES_REQUESTED' | 'SKIPPED' | 'WITHDRAWN'> =
          {
            APPROVED: 'APPROVED',
            REJECTED: 'REJECTED',
            CHANGES_REQUESTED: 'CHANGES_REQUESTED',
            SKIPPED: 'SKIPPED',
            WITHDRAWN: 'WITHDRAWN',
          };

        entries.push({
          id: `${entry.id}-review`,
          action: actionMap[entry.status] || 'APPROVED',
          userId: String(entry.reviewedBy.id),
          user: {
            id: String(entry.reviewedBy.id),
            name: entry.reviewedBy.fullName || entry.reviewedBy.email,
            email: entry.reviewedBy.email,
          },
          // a withdrawal has no message of its own (`comment` is what the sender wrote when sending)
          comment: entry.status === 'WITHDRAWN' ? undefined : entry.comment || undefined,
          variationId: entry.variationId,
          step: entry.step
            ? {
                ...entry.step,
                id: String(entry.step.id),
                workflowId: '',
                order: 0,
                approverRoles: [],
                approverUserIds: [],
                optional: false,
                createdAt: '',
                updatedAt: '',
              }
            : undefined,
          timestamp: entry.reviewedAt,
        });
      }

      return entries;
    });

    // Sort by timestamp, newest first
    history.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    return { history };
  },

  /**
   * Full drawer payload for the Release Board (all media, versions, status).
   * variationId = null/undefined loads the base post.
   */
  getReleaseDetail: async (
    postId: number,
    variationId?: number | null
  ): Promise<ReleaseDetailDto> => {
    const response = await apiClient.get<{ success: boolean; data: ReleaseDetailDto }>(
      `/api/Post/${postId}/release-detail`,
      { params: variationId ? { variationId } : undefined }
    );
    return response.data.data;
  },

  getReviewDiff: async (postId: number, variationId: number | null): Promise<ReviewDiffDto> => {
    const response = await apiClient.get<{ success: boolean; data: ReviewDiffDto }>(
      `/api/Post/${postId}/review-diff`,
      { params: variationId === null ? undefined : { variationId } }
    );
    return response.data.data;
  },

  /**
   * Server-computed release permissions of the caller for the base post and every
   * variation (the rulebook's verdicts: canEdit / canDelete / canSchedule / ...).
   */
  getPostReleasePermissions: async (postId: number): Promise<PostReleasePermissionsDto> => {
    const response = await apiClient.get<{ success: boolean; data: PostReleasePermissionsDto }>(
      `/api/Post/${postId}/release-permissions`
    );
    return response.data.data;
  },

  /**
   * Which brand a post belongs to. Backend answers 404 unless the caller has
   * release access to that brand (so no brand name leaks).
   */
  getPostBrand: async (postId: number): Promise<{ teamId: number; teamName: string }> => {
    const response = await apiClient.get<{ success: boolean; data: { teamId: number; teamName: string } }>(
      `/api/Post/${postId}/brand`
    );
    return response.data.data;
  },

  // ===========================
  // Comments
  // ===========================

  /**
   * Add a comment to a post
   */
  addComment: async (postId: string, data: AddCommentRequest): Promise<ApprovalComment> => {
    const response = await apiClient.post<{ success: boolean; data: ApprovalComment }>(
      `/api/Post/${postId}/comments`,
      {
        ...data,
        parentId: toPositiveInt(data.parentId),
        variationId: toPositiveInt(data.variationId),
      }
    );
    const created = response.data.data;
    return {
      ...created,
      stickerId: created.stickerId ?? null,
      attachments: mapAttachments(created.attachments),
      reactions: mapReactions((created as { reactions?: unknown }).reactions),
    };
  },

  getChatStickers: async (): Promise<ChatSticker[]> => {
    const response = await apiClient.get<{ success: boolean; data: ChatSticker[] }>('/api/chat-stickers');
    return response.data.data;
  },

  // ===========================
  // Comment attachments
  // ===========================

  /** Upload an image or PDF (multipart); returns the pending attachment. */
  uploadCommentAttachment: async (
    postId: number,
    file: File,
    onProgress?: (percent: number) => void,
    signal?: AbortSignal
  ): Promise<CommentAttachment> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<{ data: { attachment: unknown } }>(
      `/api/Post/${postId}/comment-attachments`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
        signal,
        onUploadProgress: (event) => {
          if (onProgress && event.total) {
            onProgress(Math.min(99, Math.round((event.loaded / event.total) * 100)));
          }
        },
      }
    );
    const attachment = mapAttachment(response.data.data.attachment);
    if (!attachment) throw new Error('Invalid attachment response');
    return attachment;
  },

  /** Step 1 of the video flow: ask for a presigned upload URL. */
  presignCommentAttachment: async (
    postId: number,
    file: { name: string; type: string; size: number }
  ): Promise<CommentAttachmentPresign> => {
    const response = await apiClient.post<{ data: CommentAttachmentPresign }>(
      `/api/Post/${postId}/comment-attachments/presign`,
      { filename: file.name, mimeType: file.type, fileSize: file.size }
    );
    return response.data.data;
  },

  /** Step 3 of the video flow: confirm the PUT finished. */
  completeCommentAttachment: async (
    postId: number,
    attachmentId: number
  ): Promise<CommentAttachment> => {
    const response = await apiClient.post<{ data: { attachment: unknown } }>(
      `/api/Post/${postId}/comment-attachments/${attachmentId}/complete`
    );
    const attachment = mapAttachment(response.data.data.attachment);
    if (!attachment) throw new Error('Invalid attachment response');
    return attachment;
  },

  /** Remove a pending attachment (uploader only). */
  deleteCommentAttachment: async (postId: number, attachmentId: number): Promise<void> => {
    await apiClient.delete(`/api/Post/${postId}/comment-attachments/${attachmentId}`);
  },

  /**
   * Step 2 of the video flow: PUT the file to the signed URL with the headers
   * from the presign response. Bare XHR (no app auth header) for real progress.
   */
  putToSignedUrl: (
    uploadUrl: string,
    uploadHeaders: Record<string, string>,
    file: File,
    onProgress: (percent: number) => void,
    signal?: AbortSignal
  ): Promise<void> =>
    new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl, true);
      Object.entries(uploadHeaders).forEach(([name, value]) => xhr.setRequestHeader(name, value));
      xhr.timeout = 0;
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress(100);
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      };
      xhr.onerror = () => reject(new Error('Upload failed: network error'));
      xhr.onabort = () => reject(new DOMException('Upload aborted', 'AbortError'));
      signal?.addEventListener('abort', () => xhr.abort(), { once: true });
      xhr.send(file);
    }),

  /**
   * People who can be @mentioned in a post's chat (excludes the caller)
   */
  getMentionable: async (postId: number, internal: boolean): Promise<MentionablePerson[]> => {
    const response = await apiClient.get<{
      success: boolean;
      data: { people: MentionablePerson[] };
    }>(`/api/Post/${postId}/mentionable`, { params: { internal } });
    return response.data.data.people ?? [];
  },

  /**
   * Get the comment thread of one version of a post (variation id, or "base" = Original)
   */
  getComments: async (
    postId: string,
    versionKey: CommentVersionKey = 'base'
  ): Promise<ApprovalComment[]> => {
    const response = await apiClient.get<CommentsResponse>(`/api/Post/${postId}/comments`, {
      params: { variationId: versionKey },
    });
    // Map backend response (fullName) to frontend format (name)
    const mapUser = (user: {
      id: number;
      fullName: string | null;
      email: string;
      isAgencyStaff?: boolean;
      releaseRole?: string | null;
    }): ApprovalUser => ({
      id: String(user.id),
      name: user.fullName || user.email || 'Unknown',
      email: user.email,
      isAgencyStaff: user.isAgencyStaff,
      releaseRole:
        user.releaseRole === 'REVIEWER'
          ? 'REVIEWER'
          : user.releaseRole === 'CREATOR'
            ? 'CREATOR'
            : null,
    });

    const mapComment = (comment: Record<string, unknown>): ApprovalComment => ({
      id: String(comment.id),
      postId: String(comment.postId),
      userId: String(comment.userId),
      content: comment.content as string,
      stickerId: typeof comment.stickerId === 'string' ? comment.stickerId : null,
      isInternal: comment.isInternal as boolean,
      parentId: comment.parentId ? String(comment.parentId) : undefined,
      variationId:
        typeof comment.variationId === 'number'
          ? comment.variationId
          : comment.variationId === null
            ? null
            : undefined,
      createdAt: comment.createdAt as string,
      updatedAt: (comment.updatedAt as string) || (comment.createdAt as string),
      user: mapUser(
        comment.user as {
          id: number;
          fullName: string | null;
          email: string;
          isAgencyStaff?: boolean;
          releaseRole?: string | null;
        }
      ),
      mentionedUserIds: Array.isArray(comment.mentionedUserIds)
        ? (comment.mentionedUserIds as unknown[]).filter(
            (id): id is number => typeof id === 'number'
          )
        : [],
      attachments: mapAttachments(comment.attachments),
      canDelete: comment.canDelete === true,
      reactions: mapReactions(comment.reactions),
      replies: ((comment.replies as Record<string, unknown>[]) || []).map(mapComment),
    });

    return (response.data.data as unknown as Record<string, unknown>[]).map(mapComment);
  },

  /**
   * Delete a comment
   */
  deleteComment: async (commentId: string): Promise<void> => {
    await apiClient.delete(`/api/comments/${commentId}`);
  },

  /**
   * Update a comment
   */
  updateComment: async (commentId: string, content: string): Promise<ApprovalComment> => {
    const response = await apiClient.put<{ success: boolean; data: ApprovalComment }>(
      `/api/comments/${commentId}`,
      { content }
    );
    const updated = response.data.data;
    return { ...updated, reactions: mapReactions((updated as { reactions?: unknown }).reactions) };
  },

  /** Add the viewer's reaction (idempotent). Returns the message's full reaction list. */
  addCommentReaction: async (commentId: number, emoji: string): Promise<CommentReaction[]> => {
    const response = await apiClient.put<CommentReactionsResponse>(
      `/api/comments/${commentId}/reactions`,
      { emoji }
    );
    return mapReactions(response.data.data?.reactions);
  },

  /** Remove the viewer's reaction (idempotent). Emoji goes in the query string (axios-safe DELETE). */
  removeCommentReaction: async (commentId: number, emoji: string): Promise<CommentReaction[]> => {
    const response = await apiClient.delete<CommentReactionsResponse>(
      `/api/comments/${commentId}/reactions`,
      { params: { emoji } }
    );
    return mapReactions(response.data.data?.reactions);
  },

  // ===========================
  // Bulk Actions
  // ===========================

  /**
   * Submit a variation for approval
   */
  submitVariationForApproval: async (
    postId: number,
    variationId: number,
    data?: SubmitForApprovalRequest
  ): Promise<PostApproval> => {
    const response = await apiClient.post<{ success: boolean; data: PostApproval }>(
      `/api/Post/${postId}/variations/${variationId}/submit-for-approval`,
      data || {}
    );
    return response.data.data;
  },

  /**
   * Bulk submit posts for approval (Send for Review)
   */
  bulkSubmitForApproval: async (
    postIds: number[],
    message?: string
  ): Promise<BulkSendResult> => {
    const response = await apiClient.post<{
      success: boolean;
      data: BulkSendResult;
    }>('/api/approvals/bulk-submit', { postIds, message });
    return response.data.data;
  },

  /**
   * Bulk approve posts (Release Posts)
   */
  bulkApprove: async (
    postIds: number[],
    comment?: string
  ): Promise<BulkApproveResult> => {
    const response = await apiClient.post<{ success: boolean; data: BulkApproveResult }>(
      '/api/approvals/bulk-approve',
      { postIds, comment }
    );
    return response.data.data;
  },

  /**
   * Undo a bulk release inside its undo window. Never all-or-nothing: the result lists
   * what was undone and what could not be (per item, with the API error code).
   */
  undoReleaseBulk: async (items: BulkUndoItem[]): Promise<BulkUndoResult> => {
    const response = await apiClient.post<{ success: boolean; data: BulkUndoResult }>(
      '/api/release/undo-bulk',
      { items }
    );
    return response.data.data;
  },
  // ===========================
  // Schedule from Approval Board
  // ===========================

  /**
   * Schedule an APPROVED post from the approval board.
   * Atomically maps the post to a calendar slot and creates a scheduled instance.
   */
  scheduleFromApproval: async (
    postId: string,
    data: ScheduleFromApprovalRequest
  ): Promise<ScheduleFromApprovalResult> => {
    const response = await apiClient.post<{ success: boolean; data: ScheduleFromApprovalResult }>(
      `/api/Post/${postId}/schedule-from-approval`,
      data
    );
    return response.data.data;
  },
};

export default approvalService;
